import * as React from 'react';
import { useEffect, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { generateUrl } from '../../../Utilities/api';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);
function scoreToBand(score) {
    if (score < 40)
        return 'red';
    if (score < 70)
        return 'yellow';
    return 'green';
}
// ── Constants ──────────────────────────────────────────────────────────────
const BRAND = {
    blue: '#00537e',
    blueDeep: '#004360',
    tint: '#EEF4F7',
    red: '#f93f43',
    yellow: '#efe300',
    green: '#138f38',
};
const BAND_COLORS = {
    red: BRAND.red,
    yellow: BRAND.yellow,
    green: BRAND.green,
};
const CATEGORY_LABELS = {
    kraft: 'Kraft',
    beweglichkeit: 'Beweglichkeit',
    gleichgewicht: 'Gleichgewicht',
    koerperzusammensetzung: 'Körperzusammensetzung',
    schmerzfreiheit: 'Schmerzfreiheit',
    stressfreiheit: 'Stressfreiheit',
    energie: 'Energie',
    ernaehrung: 'Ernährung',
    mental: 'Mental',
    environment: 'Umfeld',
    regeneration: 'Regeneration',
    training: 'Training',
};
const CATEGORY_ORDER = [
    'kraft', 'beweglichkeit', 'gleichgewicht', 'koerperzusammensetzung',
    'schmerzfreiheit', 'stressfreiheit', 'energie',
    'ernaehrung', 'mental', 'environment', 'regeneration', 'training',
];
function formatDate(iso) {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
// ── Score Ring (small inline) ───────────────────────────────────────────────
function ScoreRing({ score, size = 56 }) {
    const strokeWidth = 6;
    const band = scoreToBand(score);
    const color = BAND_COLORS[band];
    const r = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (score / 100) * circ;
    return (<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}/>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"/>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={size * 0.26} fontWeight="700" fill="#111827" style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}>
                {score}
            </text>
        </svg>);
}
// ── Category Bar ────────────────────────────────────────────────────────────
function CategoryBar({ label, score, prevScore }) {
    const band = scoreToBand(score);
    const diff = prevScore !== undefined ? score - prevScore : null;
    return (<div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 w-44 shrink-0 truncate">{label}</span>
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: BAND_COLORS[band] }}/>
            </div>
            <span className="text-sm font-bold w-8 text-right" style={{ color: BRAND.blue }}>{score}</span>
            {diff !== null && (<span className={`text-xs w-10 text-right font-medium ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                    {diff > 0 ? `+${diff}` : diff === 0 ? '—' : diff}
                </span>)}
        </div>);
}
const HealthscoreHistory = ({ testUrl, detailBaseUrl }) => {
    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedIdx, setSelectedIdx] = useState(0);
    useEffect(() => {
        void fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_history'), { credentials: 'same-origin' })
            .then(r => r.json())
            .then((data) => { setRecords(data); setIsLoading(false); })
            .catch(() => setIsLoading(false));
    }, []);
    if (isLoading) {
        return (<div className="flex flex-col gap-6 animate-pulse">
                <div className="h-48 bg-gray-100 rounded-xl"/>
                <div className="h-32 bg-gray-100 rounded-xl"/>
            </div>);
    }
    if (records.length === 0) {
        return (<div className="flex flex-col items-center justify-center py-16 gap-4">
                <p className="text-gray-500 text-center max-w-xs">
                    Du hast noch keinen Healthscore-Test abgeschlossen.
                </p>
                <a href={testUrl} className="text-sm font-bold px-4 py-2 rounded-lg text-white" style={{ backgroundColor: BRAND.blue }}>
                    Jetzt Test starten
                </a>
            </div>);
    }
    const selected = records[selectedIdx];
    const prev = records[selectedIdx + 1];
    // Chart: oldest → newest (records are newest-first)
    const chartRecords = [...records].reverse();
    const chartLabels = chartRecords.map(r => formatDate(r.finishedAt));
    const chartScores = chartRecords.map(r => r.score);
    const chartData = {
        labels: chartLabels,
        datasets: [{
                label: 'Gesamt-Score',
                data: chartScores,
                borderColor: BRAND.blue,
                backgroundColor: BRAND.tint,
                fill: true,
                tension: 0.3,
                pointBackgroundColor: chartScores.map(s => BAND_COLORS[scoreToBand(s)]),
                pointRadius: 5,
                pointHoverRadius: 7,
            }],
    };
    const chartOptions = {
        responsive: true,
        scales: {
            y: { min: 0, max: 100, grid: { color: '#f3f4f6' } },
            x: { grid: { display: false } },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` Score: ${ctx.raw}`,
                },
            },
        },
        onClick: (_, elements) => {
            if (elements.length > 0) {
                // chart is reversed (oldest first), records is newest first
                setSelectedIdx(records.length - 1 - elements[0].index);
            }
        },
    };
    return (<div className="flex flex-col gap-6">

            {/* Chart */}
            <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Verlauf</p>
                        <p className="text-sm text-gray-500">{records.length} Test{records.length !== 1 ? 's' : ''}</p>
                    </div>
                    <a href={testUrl} className="text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>
                        Neuen Test starten
                    </a>
                </div>
                <Line data={chartData} options={chartOptions}/>
            </div>

            {/* History list + detail */}
            <div className="flex gap-4 flex-col md:flex-row">

                {/* List */}
                <div className="flex flex-col gap-2 md:w-64 shrink-0">
                    {records.map((r, idx) => {
            const band = scoreToBand(r.score);
            const isSelected = idx === selectedIdx;
            return (<button key={r.token} type="button" onClick={() => setSelectedIdx(idx)} className="flex items-center gap-3 p-3 rounded-xl text-left transition-colors w-full" style={isSelected
                    ? { backgroundColor: BRAND.tint, borderLeft: `3px solid ${BRAND.blue}` }
                    : { backgroundColor: 'white', borderLeft: '3px solid transparent' }}>
                                <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: BAND_COLORS[band] }}/>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold" style={{ color: BRAND.blueDeep }}>
                                        Score {r.score}
                                    </p>
                                    <p className="text-xs text-gray-400">{formatDate(r.finishedAt)}</p>
                                </div>
                                {idx === 0 && (<span className="text-xs font-semibold px-1.5 py-0.5 rounded-full shrink-0" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>Aktuell</span>)}
                            </button>);
        })}
                </div>

                {/* Detail */}
                <div className="flex-1 bg-white rounded-xl shadow-sm p-5">
                    <div className="flex items-center gap-4 mb-5">
                        <ScoreRing score={selected.score} size={72}/>
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                {selectedIdx === 0 ? 'Aktueller Test' : `Test vom ${formatDate(selected.finishedAt)}`}
                            </p>
                            <p className="text-2xl font-bold" style={{ color: BRAND.blueDeep }}>
                                Score {selected.score}
                            </p>
                            {prev !== undefined && (<p className="text-sm text-gray-400 mt-0.5">
                                    {selected.score > prev.score
                ? `+${selected.score - prev.score} gegenüber vorherigem Test`
                : selected.score < prev.score
                    ? `${selected.score - prev.score} gegenüber vorherigem Test`
                    : 'Kein Unterschied zum vorherigen Test'}
                                </p>)}
                        </div>
                    </div>

                    <a href={`${detailBaseUrl}/${selected.token}`} className="inline-block text-sm font-semibold px-3 py-1.5 rounded-lg" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>
                        Detailansicht
                    </a>

                    {Object.keys(selected.groupScores).length > 0 && (<div className="flex flex-col gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Kategorien</p>
                            {CATEGORY_ORDER
                .filter(k => selected.groupScores[k] !== undefined)
                .map(k => (<CategoryBar key={k} label={CATEGORY_LABELS[k] ?? k} score={Math.round(selected.groupScores[k])} prevScore={prev?.groupScores[k] !== undefined
                    ? Math.round(prev.groupScores[k])
                    : undefined}/>))}
                        </div>)}
                </div>
            </div>
        </div>);
};
HealthscoreHistory.displayName = 'HealthscoreHistory';
export default HealthscoreHistory;
